/**
 * Google Places Autocomplete Constants
 * Configuration values and component type mappings for Google Maps Places API
 *
 * @see https://developers.google.com/maps/documentation/javascript/place-autocomplete
 */

/**
 * Timing configuration for Google Places initialization and interactions
 */
export const GOOGLE_PLACES_TIMING = {
  /** Delay before retrying initialization if Google Maps API is not ready */
  RETRY_DELAY_MS: 200,

  /** Initial delay before starting initialization (ensures DOM is ready) */
  INIT_DELAY_MS: 500,

  /** Delay after address selection before swapping to manual input (Shopify pattern) */
  INPUT_SWAP_DELAY_MS: 100,
};

/**
 * Google Places API configuration
 * Supports both Legacy API (Autocomplete) and AutocompleteService (Programmatic API)
 */
export const GOOGLE_PLACES_CONFIG = {
  /** Event name for place selection in Autocomplete (Legacy API) */
  EVENT_NAME: 'place_changed',

  /**
   * Autocomplete options for google.maps.places.Autocomplete constructor (Legacy API)
   * @see https://developers.google.com/maps/documentation/javascript/reference/places-widget#AutocompleteOptions
   */
  AUTOCOMPLETE_OPTIONS: {
    types: ['address'], // Restrict to addresses only
    componentRestrictions: { country: 'us' }, // US only
    fields: ['address_components', 'formatted_address', 'geometry'], // Minimal fields for cost efficiency
  },

  /**
   * AutocompleteService options (Programmatic API - Recommended)
   * @see https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletionRequest
   */
  AUTOCOMPLETE_SERVICE_OPTIONS: {
    types: ['address'], // Restrict to addresses only
    componentRestrictions: { country: 'us' }, // US only
  },

  /**
   * PlacesService fields for Place Details request
   * @see https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceDetailsRequest
   */
  PLACE_DETAILS_FIELDS: ['address_components', 'formatted_address', 'geometry'],

  /** Library name to import from Google Maps */
  LIBRARY_NAME: 'places',
};

/**
 * Google Places address component types
 * Maps Google's component types to readable names
 *
 * @see https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
 */
export const ADDRESS_COMPONENT_TYPES = {
  /** Street number (e.g., "222") */
  STREET_NUMBER: 'street_number',

  /** Street name (e.g., "South Copeland Street") */
  ROUTE: 'route',

  /** City/locality (e.g., "Tallahassee") */
  LOCALITY: 'locality',

  /** State/province (e.g., "Florida" or "FL") */
  STATE: 'administrative_area_level_1',

  /** ZIP/postal code (e.g., "32306") */
  POSTAL_CODE: 'postal_code',

  /** Country (e.g., "United States" or "US") */
  COUNTRY: 'country',
};

/**
 * Structure for parsed address components
 * Default empty address object
 */
export const EMPTY_ADDRESS = {
  streetNumber: '',
  route: '',
  city: '',
  state: '',
  stateCode: '',
  zip: '',
  country: '',
  countryCode: '',
};

/**
 * Analytics event configuration
 */
export const ANALYTICS_CONFIG = {
  /** Event name for GTM/dataLayer when address autocomplete is used */
  EVENT_NAME: 'address_autocomplete_used',

  /** Source identifier for analytics tracking (Legacy API) */
  SOURCE_LEGACY: 'google_places_legacy_api',

  /** Source identifier for analytics tracking (AutocompleteService) */
  SOURCE_AUTOCOMPLETE_SERVICE: 'google_places_autocomplete_service',
};

/**
 * Debug mode - set to true to enable verbose console logging
 * In production, this should be controlled by environment variable
 */
export const DEBUG_MODE = process.env.NODE_ENV === 'development';
